import { supabase } from '@/integrations/supabase/client';
import { mapearDadosCNPJ, type CNPJData } from '@/utils/cnpj';

export interface ClienteExistente {
  cliente_id: string;
  razao_social: string;
  cnpj: string;
  owner_consultor_id: string | null;
  consultor_nome: string | null;
}

export async function buscarClientePorCNPJ(cnpj: string): Promise<ClienteExistente | null> {
  const cnpjNormalizado = cnpj.replace(/\D/g, '');
  
  const { data, error } = await supabase
    .rpc('buscar_cliente_por_cnpj', { p_cnpj: cnpjNormalizado });

  if (error) {
    console.error('❌ Erro RPC buscar_cliente_por_cnpj:', error);
    throw new Error('Erro ao verificar se cliente existe');
  }

  return data?.[0] || null;
}

export async function criarClienteFromCNPJ(
  cnpjData: ReturnType<typeof mapearDadosCNPJ>,
  consultorId: string
) {
  try {
    // 0. Validar se consultor existe na tabela usuarios
    const { data: consultor, error: consultorError } = await supabase
      .from('usuarios')
      .select('id')
      .eq('id', consultorId)
      .single();

    if (consultorError || !consultor) {
      console.error('Consultor não encontrado:', consultorError);
      throw new Error('Consultor não encontrado na base de dados');
    }

    // 1. Criar cliente
    const { data: cliente, error: clienteError } = await supabase
      .from('clientes')
      .insert({
        cnpj: cnpjData.cnpj,
        razao_social: cnpjData.razao_social,
        nome_fantasia: cnpjData.nome_fantasia,
        situacao_cadastral: cnpjData.situacao_cadastral,
        data_inicio_atividade: cnpjData.data_inicio_atividade,
        cnae_principal: cnpjData.cnae_principal,
        cnae_descricao: cnpjData.cnae_descricao,
        classificacao_cliente: 'Fresh'
      })
      .select()
      .single();

    if (clienteError) {
      console.error('Erro ao criar cliente:', clienteError);
      throw new Error('Erro ao criar cliente');
    }

    // 2. Criar entrada na tabela de donos
    const { error: donoError } = await supabase
      .from('donos_cliente')
      .insert({
        cliente_id: cliente.id,
        owner_consultor_id: consultorId
      });

    if (donoError) {
      console.error('Erro ao criar dono do cliente:', donoError);
      // Tentar remover cliente criado
      await supabase.from('clientes').delete().eq('id', cliente.id);
      throw new Error('Erro ao atribuir cliente ao consultor');
    }

    // 3. Criar contatos do cliente
    const { error: contatoError } = await supabase
      .from('contatos_cliente')
      .insert({
        cliente_id: cliente.id,
        telefone_principal: cnpjData.telefone_principal || null,
        email_principal: cnpjData.email_principal || null,
        endereco_rua: cnpjData.endereco_rua || null,
        endereco_numero: cnpjData.endereco_numero || null,
        endereco_bairro: cnpjData.endereco_bairro || null,
        endereco_cep: cnpjData.endereco_cep || null,
        endereco_cidade: cnpjData.endereco_cidade || null,
        endereco_uf: cnpjData.endereco_uf || null,
        // Todos os certificados iniciam como false
        telefone_certificado: false,
        email_certificado: false,
        whatsapp_certificado: false
      });

    if (contatoError) {
      console.error('Erro ao criar contatos:', contatoError);
      // Tentar limpar dados criados
      await supabase.from('donos_cliente').delete().eq('cliente_id', cliente.id);
      await supabase.from('clientes').delete().eq('id', cliente.id);
      throw new Error('Erro ao criar contatos do cliente');
    }

    console.log('Contato do cliente criado');

    // 4. Criar empresa
    const { data: empresa, error: empresaError } = await supabase
      .from('empresa')
      .insert({
        cnpj: cnpjData.cnpj,
        razao_social: cnpjData.razao_social,
        nome_fantasia: cnpjData.nome_fantasia || null,
        telefone: cnpjData.telefone_principal || null,
        email: cnpjData.email_principal || null,
        endereco: {
          rua: cnpjData.endereco_rua || '',
          numero: cnpjData.endereco_numero || '',
          bairro: cnpjData.endereco_bairro || '',
          cep: cnpjData.endereco_cep || '',
          cidade: cnpjData.endereco_cidade || '',
          uf: cnpjData.endereco_uf || ''
        },
        origem: 'api_receita',
        ativo: true
      })
      .select()
      .single();

    if (empresaError) {
      console.error('Erro ao criar empresa:', empresaError);
      // Tentar limpar dados criados
      await supabase.from('contatos_cliente').delete().eq('cliente_id', cliente.id);
      await supabase.from('donos_cliente').delete().eq('cliente_id', cliente.id);
      await supabase.from('clientes').delete().eq('id', cliente.id);
      throw new Error('Erro ao criar empresa');
    }

    console.log('Empresa criada:', empresa);

    return { cliente, empresa };

  } catch (error) {
    console.error('Erro no processo de criação do cliente:', error);
    throw error;
  }
}

export async function atualizarClienteFromCNPJ(
  clienteId: string,
  cnpjData: ReturnType<typeof mapearDadosCNPJ>,
  forcarAtualizacao = false
) {
  try {
    // 1. Atualizar dados do cliente
    const { error: clienteError } = await supabase
      .from('clientes')
      .update({
        cnpj: cnpjData.cnpj,
        razao_social: cnpjData.razao_social,
        nome_fantasia: cnpjData.nome_fantasia,
        situacao_cadastral: cnpjData.situacao_cadastral,
        data_inicio_atividade: cnpjData.data_inicio_atividade,
        cnae_principal: cnpjData.cnae_principal,
        cnae_descricao: cnpjData.cnae_descricao
      })
      .eq('id', clienteId);

    if (clienteError) {
      console.error('Erro ao atualizar cliente:', clienteError);
      throw new Error('Erro ao atualizar dados do cliente');
    }

    // 2. Buscar ou criar contatos
    const { data: contatoAtual, error: contatoError } = await supabase
      .from('contatos_cliente')
      .select('*')
      .eq('cliente_id', clienteId)
      .maybeSingle();

    if (contatoError && contatoError.code !== 'PGRST116') {
      console.error('Erro ao buscar contatos atuais:', contatoError);
      throw new Error('Erro ao verificar contatos atuais');
    }

    // Se não existir contato, CRIAR registro
    if (!contatoAtual) {
      console.log('⚠️ Contato não existe, criando novo registro...');
      
      const novoContato = {
        cliente_id: clienteId,
        telefone_principal: cnpjData.telefone_principal || null,
        email_principal: cnpjData.email_principal || null,
        endereco_rua: cnpjData.endereco_rua || null,
        endereco_numero: cnpjData.endereco_numero || null,
        endereco_bairro: cnpjData.endereco_bairro || null,
        endereco_cep: cnpjData.endereco_cep || null,
        endereco_cidade: cnpjData.endereco_cidade || null,
        endereco_uf: cnpjData.endereco_uf || null,
        telefone_certificado: false,
        email_certificado: false,
        whatsapp_certificado: false
      };

      const { error: insertError } = await supabase
        .from('contatos_cliente')
        .insert(novoContato);
      
      if (insertError) {
        console.error('❌ Erro ao criar contato:', insertError);
        throw new Error(`Erro ao criar registro de contatos: ${insertError.message}`);
      }
      
      console.log('✅ Registro de contato criado');
      return true;
    }

    // 3. Preparar dados de atualização dos contatos
    const updateData: any = {
      endereco_rua: cnpjData.endereco_rua || null,
      endereco_numero: cnpjData.endereco_numero || null,
      endereco_bairro: cnpjData.endereco_bairro || null,
      endereco_cep: cnpjData.endereco_cep || null,
      endereco_cidade: cnpjData.endereco_cidade || null,
      endereco_uf: cnpjData.endereco_uf || null
    };

    // Só atualizar email se não certificado ou se forçar atualização
    if (!contatoAtual.email_certificado || forcarAtualizacao) {
      updateData.email_principal = cnpjData.email_principal || null;
    }

    // Só atualizar telefone se não certificado ou se forçar atualização
    if (!contatoAtual.telefone_certificado || forcarAtualizacao) {
      updateData.telefone_principal = cnpjData.telefone_principal || null;
    }

    // 4. Atualizar contatos
    const { error: updateContatoError } = await supabase
      .from('contatos_cliente')
      .update(updateData)
      .eq('cliente_id', clienteId);

    if (updateContatoError) {
      console.error('Erro ao atualizar contatos:', updateContatoError);
      throw new Error('Erro ao atualizar contatos do cliente');
    }

    return true;

  } catch (error) {
    console.error('Erro no processo de atualização do cliente:', error);
    throw error;
  }
}

export async function compararDadosCliente(
  clienteId: string,
  novosDados: ReturnType<typeof mapearDadosCNPJ>
) {
  // Buscar dados atuais do cliente e contatos
  const { data: clienteAtual, error: clienteError } = await supabase
    .from('clientes')
    .select('*')
    .eq('id', clienteId)
    .single();

  const { data: contatoAtual, error: contatoError } = await supabase
    .from('contatos_cliente')
    .select('*')
    .eq('cliente_id', clienteId)
    .maybeSingle();

  if (clienteError) {
    console.error('❌ Erro ao buscar cliente:', clienteError);
    throw new Error('Erro ao buscar dados atuais do cliente');
  }

  console.log('✅ Dados atuais do cliente:', {
    cliente: clienteAtual?.razao_social,
    temContato: !!contatoAtual
  });

  const diferencas: Array<{
    campo: string;
    valorAtual: string;
    valorNovo: string;
    podeAtualizar: boolean;
    motivo?: string;
  }> = [];

  // Comparar campos do cliente
  const camposCliente = [
    { key: 'razao_social', label: 'Razão Social' },
    { key: 'nome_fantasia', label: 'Nome Fantasia' },
    { key: 'situacao_cadastral', label: 'Situação Cadastral' },
    { key: 'cnae_principal', label: 'CNAE Principal' },
    { key: 'cnae_descricao', label: 'Descrição CNAE' }
  ];

  camposCliente.forEach(campo => {
    const valorAtual = clienteAtual[campo.key] || '';
    const valorNovoRaw = novosDados[campo.key as keyof typeof novosDados];
    const valorNovo = typeof valorNovoRaw === 'string' ? valorNovoRaw : '';
    
    if (valorAtual !== valorNovo && valorNovo !== '') {
      diferencas.push({
        campo: campo.label,
        valorAtual,
        valorNovo,
        podeAtualizar: true
      });
    }
  });

  // Comparar campos de contato (apenas se contato existir)
  if (contatoAtual) {
    const camposContato = [
      { 
        key: 'email_principal', 
        label: 'Email', 
        certificado: contatoAtual.email_certificado 
      },
      { 
        key: 'telefone_principal', 
        label: 'Telefone', 
        certificado: contatoAtual.telefone_certificado 
      }
    ];

    camposContato.forEach(campo => {
      const valorAtual = contatoAtual[campo.key] || '';
      const valorNovoRaw = novosDados[campo.key as keyof typeof novosDados];
      const valorNovo = typeof valorNovoRaw === 'string' ? valorNovoRaw : '';
      
      if (valorAtual !== valorNovo && valorNovo !== '') {
        diferencas.push({
          campo: campo.label,
          valorAtual,
          valorNovo,
          podeAtualizar: !campo.certificado,
          motivo: campo.certificado ? 'Campo certificado - requer permissão especial' : undefined
        });
      }
    });

    // Comparar endereço
    const camposEndereco = [
      { key: 'endereco_rua', label: 'Logradouro' },
      { key: 'endereco_numero', label: 'Número' },
      { key: 'endereco_bairro', label: 'Bairro' },
      { key: 'endereco_cep', label: 'CEP' },
      { key: 'endereco_cidade', label: 'Cidade' },
      { key: 'endereco_uf', label: 'UF' }
    ];

    camposEndereco.forEach(campo => {
      const valorAtual = contatoAtual[campo.key] || '';
      const valorNovoRaw = novosDados[campo.key as keyof typeof novosDados];
      const valorNovo = typeof valorNovoRaw === 'string' ? valorNovoRaw : '';
      
      if (valorAtual !== valorNovo && valorNovo !== '') {
        diferencas.push({
          campo: campo.label,
          valorAtual,
          valorNovo,
          podeAtualizar: true
        });
      }
    });
  } else {
    // Se não há contato, todos os dados novos são diferenças
    console.log('⚠️ Cliente sem contatos cadastrados - todos os dados serão adicionados');
  }

  return diferencas;
}

// Função removida - usar api_criar_pedido_com_itens RPC em vez disso